import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import type { FC } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { InputField } from 'src/components/input-field';
import { useDialog } from 'src/hooks/use-dialog';
import EditIcon from '@material-ui/icons/Edit';
import { ConfirmationDialog } from 'src/components/confirmation-dialog';
import { ServiceType } from 'src/interfaces/new-channel-partner/channelPartner';
import { serviceMasterApi } from 'src/api/serviceType';
import { ResponseCodes } from 'src/enums/responseCodes';
import { ServiceTypeModel } from 'src/interfaces/masters/service/service';
import { MenuProps } from 'src/components/masters/customCss';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { Status } from 'src/enums/common';
import { getServiceNameById } from 'src/components/masters/customFunctions';
import { ChevronLeft as ChevronLeftIcon } from '../../../../../icons/chevron-left';
import { ChevronDown as ChevronDownIcon } from '../../../../../icons/chevron-down';
import { Scrollbar } from '../../../../scrollbar';
import { Trash as TrashIcon } from '../../../../../icons/trash';

interface ServiceTypeProps {
  serviceTypes: ServiceType[];
  addService: (data: ServiceType) => Promise<boolean>;
  updateService: (data: ServiceType) => Promise<boolean>;
  deleteService: (data: string) => void;
}

export const ServiceInfoCard: FC<ServiceTypeProps> = (props) => {
  const { serviceTypes, addService,
    updateService, deleteService } = props;
  const [allServices, setAllServices] = useState<ServiceTypeModel[]>([]);
  const [finalServices, setFinalServices] = useState<ServiceType[]>([]);
  const [serviceToBeDeleted, setServiceToBeDeleted] = useState<ServiceType>();
  const [isEditClicked, setIsEditClicked] = useState<boolean>(false);
  const [addButtonClicked, setAddButtonClicked] = useState(false);
  const [displayBankInfo, setDisplayBankInfo] = useState<boolean>(true);
  const [addServiceClicked, setAddServiceClicked] = useState(false);

  const serviceToBeDeleteDetails = (service: ServiceType) => {
    setServiceToBeDeleted(service);
  };
  const [
    deleteDialogOpen,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog
  ] = useDialog();

  useEffect(() => {
    if (serviceToBeDeleted) {
      handleOpenDeleteDialog();
    }
  }, [serviceToBeDeleted]);

  const handleDeleteService = async () => {
    handleCloseDeleteDialog();
    await deleteService(serviceToBeDeleted.id);
  };

  const getAllServices = async () => {
    const paginationObject: PaginationAndFilter = {
      filters: [
        {
          property: 'status',
          operator: 'in',
          value: [
            Status.PUBLISH,
          ],
        },
      ],
      pageSize: 99,
      pageNumber: 1,
      sort: -1,
      sortBy: ''
    };
    const serviceResponse = await serviceMasterApi.getServiceByPaginationAndFilters(
      paginationObject
    );
    if (serviceResponse.statusCode === ResponseCodes.OK) {
      const selectedItems = serviceTypes?.map((x) => x.id);
      const availableServices = serviceResponse?.responseData?.records?.filter(
        (service) => !selectedItems.includes(service.id)
      );
      setAllServices(availableServices);
      setFinalServices(serviceTypes);
    }
  };

  useEffect(() => {
    getAllServices().catch(console.error);
  }, [serviceTypes]);

  const formik = useFormik({
    initialValues: {
      id: '',
      type: '',
      dailyCallCapacity: '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      type: Yup.string().required('Service Type is Required'),
      dailyCallCapacity: Yup.number().integer('Must be an integer').typeError('Must be a number')
        .moreThan(0, 'Should be greater than 0')
        .required('Call Capacity is Required')
    }),

    onSubmit: async (values, helpers) => {
      try {
        const addServiceData : ServiceType = {
          id: isEditClicked ? values.id : values.type,
          type: getServiceNameById(allServices, values.type),
          dailyCallCapacity: values.dailyCallCapacity
        };
        let result;
        if (isEditClicked) {
          result = await updateService(addServiceData);
        } else {
          result = await addService(addServiceData);
        }
        if (result) {
          helpers.resetForm();
          setIsEditClicked(false);
          setAddServiceClicked(false);
          setAddButtonClicked(false);
        }
      } catch (err) {
        console.error(err);
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    }
  });

  const onClickEditButton = (service: ServiceType) => {
    setFinalServices(finalServices.filter((item) => item.id !== service.id));
    formik.setFieldValue('id', service.id);
    formik.setFieldValue('type', service.type);
    formik.setFieldValue('dailyCallCapacity', service.dailyCallCapacity);
    setIsEditClicked(true);
    setAddButtonClicked(true);
  };

  const handleChange = (event) => {
    formik.handleChange(event);
    formik.setFieldValue('id', event.target.value);
  };

  const onCancel = () => {
    setFinalServices(serviceTypes);
    formik.resetForm();
    setAddServiceClicked(false);
    setAddButtonClicked(false);
    setIsEditClicked(false);
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mt: 1
        }}
      >
        <CardHeader
          titleTypographyProps={{ variant: 'h2' }}
          title="Services"
          action={(
            <>
              {serviceTypes.length < 1 && (
              <Button
                color="primary"
                size="small"
                disabled={addButtonClicked || allServices.length === 0}
                onClick={() => {
                  setAddServiceClicked(true);
                  setDisplayBankInfo(true);
                  setAddButtonClicked(true);
                }}
                variant="contained"
              >
                Add
              </Button>
              )}
              { serviceTypes.length > 0 && (
                <>
                  { displayBankInfo && (
                  <IconButton
                    sx={{ py: 0 }}
                    aria-label="view"
                    onClick={() => {
                      setDisplayBankInfo(false);
                    }}
                  >
                    <ChevronDownIcon />
                  </IconButton>
                  )}
                  { !displayBankInfo && (
                  <IconButton
                    sx={{ py: 0 }}
                    aria-label="view"
                    onClick={() => {
                      setDisplayBankInfo(true);
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  )}
                </ >
              )}
            </>
          )}
        />
        {displayBankInfo && (
          <>
            {(serviceTypes.length > 0 || addButtonClicked) && (
            <CardContent>
              <Grid
                container
                spacing={1}
              >
                <Grid
                  item
                  md={12}
                  xs={12}
                  sm={12}
                >
                  <Card
                    variant="outlined"
                  >
                    <Scrollbar>
                      <TableContainer
                        sx={{
                          maxHeight: '220px',
                        }}
                      >
                        <Table
                          size="small"
                          stickyHeader
                          aria-label="sticky table"
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell
                                width="10%"
                                sx={{
                                  backgroundColor: 'neutral.100',
                                }}
                              >
                                SERVICE ID
                              </TableCell>
                              <TableCell
                                width="40%"
                                sx={{
                                  backgroundColor: 'neutral.100',
                                }}
                              >
                                TYPE
                              </TableCell>
                              <TableCell
                                width="35%"
                                sx={{
                                  backgroundColor: 'neutral.100',
                                }}
                                align="center"
                              >
                                DAILY CALL CAPACITY
                              </TableCell>
                              <TableCell
                                width="15%"
                                sx={{
                                  backgroundColor: 'neutral.100',
                                }}
                                align="center"
                              >
                                ACTION
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {finalServices?.map((service) => (
                              <TableRow
                                key={service.id}
                              >
                                <TableCell>
                                  {service.id}
                                </TableCell>
                                <TableCell>
                                  {service.type}
                                </TableCell>
                                <TableCell
                                  align="center"
                                >
                                  {service.dailyCallCapacity}
                                </TableCell>
                                <TableCell
                                  align="center"
                                >
                                  <Box>
                                    <IconButton
                                      title="Edit Daily Call Capacity"
                                      disabled={isEditClicked}
                                      aria-label="edit"
                                      onClick={() => {
                                        onClickEditButton(service);
                                      }}
                                      style={{ padding: 0, marginRight: 2 }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    {' '}
                                    |
                                    {' '}
                                    <IconButton
                                      title="Delete Service"
                                      disabled={isEditClicked}
                                      color="primary"
                                      onClick={() => {
                                        serviceToBeDeleteDetails(service);
                                      }}
                                      type="button"
                                      style={{ padding: 0 }}
                                    >
                                      <TrashIcon />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>

                      </TableContainer>
                    </Scrollbar>
                    <Table
                      size="small"
                    >
                      <TableBody>
                        {(addServiceClicked || isEditClicked) && (
                        <TableRow
                          sx={{
                            borderTop: '1px',
                            borderTopColor: 'lightgrey',
                            borderTopStyle: 'solid'
                          }}
                          hover
                        >
                          <TableCell
                            width="10%"
                          >
                            {formik.values.id || ' --'}
                          </TableCell>
                          { isEditClicked ? (
                            <TableCell
                              width="40%"
                            >
                              {formik.values.type}
                            </TableCell>
                          ) : (
                            <TableCell
                              width="40%"
                            >
                              <InputField
                                error={Boolean(formik.touched.type
                                          && formik.errors.type)}
                                style={{ maxWidth: 230, minWidth: 200 }}
                                helperText={formik.touched.type
                                          && formik.errors.type}
                                placeholder="Service Type"
                                name="type"
                                required
                                onBlur={formik.handleBlur}
                                onChange={handleChange}
                                select
                                value={formik.values.type}
                                SelectProps={{ MenuProps }}
                              >
                                {allServices?.map((service) => (
                                  <MenuItem
                                    key={service.id}
                                    value={service.id}
                                  >
                                    {service.type}
                                  </MenuItem>
                                ))}
                              </InputField>
                            </TableCell>
                          )}
                          <TableCell
                            align="center"
                            width="35%"
                          >
                            <InputField
                              error={Boolean(formik.touched.dailyCallCapacity
                                  && formik.errors.dailyCallCapacity)}
                              style={{ maxWidth: 130 }}
                              helperText={formik.touched.dailyCallCapacity
                                  && formik.errors.dailyCallCapacity}
                              label=""
                              type="number"
                              placeholder="Call Capacity"
                              name="dailyCallCapacity"
                              onBlur={formik.handleBlur}
                              onChange={(event) => {
                                if (event.target.value !== ' ') {
                                  formik.setFieldValue('dailyCallCapacity', event.target.value);
                                }
                              }}
                              required
                              value={formik.values.dailyCallCapacity}
                            />
                          </TableCell>
                          <TableCell
                            align="center"
                            width="15%"
                          >
                            <Button
                              sx={{
                                width: '6px'
                              }}
                              color="primary"
                              size="small"
                              disabled={(!(formik.isValid && formik.dirty)
                                      || formik.isSubmitting)}
                              onClick={() => { formik.handleSubmit(); }}
                              variant="contained"
                            >
                              { isEditClicked ? 'Update' : 'Add'}
                            </Button>
                            <Button
                              color="primary"
                              sx={{
                                padding: 0
                              }}
                              size="small"
                              onClick={onCancel}
                              variant="text"
                            >
                              Cancel
                            </Button>
                          </TableCell>
                        </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>

            )}
          </ >
        )}
      </Card>
      <ConfirmationDialog
        message="Are you really want to delete the Service?"
        onCancel={() => {
          handleCloseDeleteDialog();
        }}
        onConfirm={handleDeleteService}
        open={deleteDialogOpen}
        title="Are You Sure?"
        variant="error"
      />
    </>
  );
};

ServiceInfoCard.propTypes = {
  serviceTypes: PropTypes.array,
  addService: PropTypes.func,
  updateService: PropTypes.func,
  deleteService: PropTypes.func
};
